import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Menu, X, LogIn, LayoutDashboard, Bot } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { useI18n } from "../../i18n/I18nProvider";
import { useAuth } from "../../context/AuthContext";
import { ASSETS } from "../../lib/assets";
import { LanguageSelect } from "./LanguageSelect";
import { SearchDialog } from "./SearchDialog";
import { WalletButton } from "../token/WalletButton";

export const NAV = [["home", "/"], ["world", "/world"], ["learn", "/learn"], ["library", "/library"], ["community", "/community"], ["impact", "/impact"], ["about", "/about"], ["token", "/token"]];

export function Header() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const k = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearch(true); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  const linkCls = ({ isActive }) => `whitespace-nowrap rounded-full px-2.5 py-2 text-[15px] font-bold transition-colors ${isActive ? "bg-[#EAF7FF] text-[#237BE8]" : "text-[#17345F] hover:text-[#237BE8]"}`;

  return (
    <header className="sticky top-0 z-50 border-b border-sky-100 bg-white/90 backdrop-blur-xl">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:rounded-full focus:bg-white focus:px-4 focus:py-2">Skip to content</a>
      <div className="mx-auto flex max-w-[1440px] items-center gap-3 px-4 py-2.5 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex shrink-0 items-center gap-2" aria-label="学无界 Xue Wujie home">
          <img src={ASSETS.logo.src} alt={ASSETS.logo.alt} width={48} height={48} className="h-12 w-12 object-contain" />
          <span className="hidden font-heading text-lg font-black leading-none text-[#17345F] sm:block">学无界<span className="block text-[11px] font-bold tracking-wide text-[#237BE8]">XUE WUJIE</span></span>
        </Link>
        <nav aria-label="Main" className="hidden flex-1 items-center justify-center gap-0.5 xl:flex">
          {NAV.map(([k, to]) => <NavLink key={k} to={to} end={to === "/"} data-testid={`nav-${k}-link`} className={linkCls}>{t(`nav.${k}`)}</NavLink>)}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="nav-search-btn" onClick={() => setSearch(true)} aria-label={t("nav.search")} className="grid h-10 w-10 place-items-center rounded-full border border-sky-200 bg-white text-[#17345F] hover:bg-[#EAF7FF]"><Search className="h-4 w-4" /></button>
          <div className="hidden md:block"><LanguageSelect /></div>
          <div className="hidden min-[1780px]:block"><WalletButton compact /></div>
          <Link to={user ? "/dashboard" : "/login"} data-testid="nav-account-link" aria-label={user ? t("nav.dashboard") : t("nav.signin")} className="hidden h-10 items-center gap-2 whitespace-nowrap rounded-full border border-sky-200 bg-white px-3 text-sm font-bold text-[#17345F] hover:bg-[#EAF7FF] md:flex">
            {user ? <><LayoutDashboard className="h-4 w-4" />{t("nav.dashboard")}</> : <><LogIn className="h-4 w-4" />{t("nav.signin")}</>}
          </Link>
          <Link to="/world" data-testid="nav-cta-btn" className="btn-pill btn-primary hidden whitespace-nowrap lg:inline-flex xl:hidden min-[1560px]:inline-flex">{t("nav.cta")}</Link>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} aria-label={t("nav.menu")} aria-expanded={open} className="grid h-10 w-10 place-items-center rounded-full bg-[#237BE8] text-white xl:hidden"><Menu className="h-5 w-5" /></button>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" data-testid="mobile-menu" className="w-[88vw] max-w-sm overflow-y-auto bg-white p-6 [&>button]:hidden">
          <div className="mb-6 flex items-center justify-between">
            <SheetTitle className="font-heading text-xl font-black text-[#17345F]">{t("nav.menu")}</SheetTitle>
            <SheetDescription className="sr-only">Site navigation</SheetDescription>
            <button data-testid="mobile-menu-close-btn" onClick={() => setOpen(false)} aria-label={t("nav.close")} className="grid h-11 w-11 place-items-center rounded-full bg-[#EAF7FF]"><X className="h-5 w-5" /></button>
          </div>
          <nav aria-label="Mobile" className="flex flex-col gap-1">
            {[...NAV, ["dashboard", "/dashboard"], ["tutor", "/ai-tutor"], ["contribute", "/contribute"], ["docs", "/docs"], ["faq", "/faq"], ["contact", "/contact"]].map(([k, to]) => (
              <NavLink key={k} to={to} end={to === "/"} data-testid={`mobile-nav-${k}-link`} className={({ isActive }) => `rounded-2xl px-4 py-3 text-base font-bold ${isActive ? "bg-[#EAF7FF] text-[#237BE8]" : "text-[#17345F]"}`}>{t(`nav.${k}`)}</NavLink>
            ))}
          </nav>
          <div className="mt-6 flex flex-col gap-3">
            <LanguageSelect testid="mobile-language-select" />
            {!user && <Link to="/login" data-testid="mobile-signin-link" className="btn-pill btn-ghost justify-center"><LogIn className="h-4 w-4" />{t("nav.signin")}</Link>}
            <Link to="/ai-tutor" data-testid="mobile-tutor-link" className="btn-pill btn-ghost justify-center"><Bot className="h-4 w-4" />{t("nav.tutor")}</Link>
            <WalletButton compact />
            <Link to="/world" data-testid="mobile-cta-btn" className="btn-pill btn-primary justify-center">{t("nav.cta")}</Link>
          </div>
        </SheetContent>
      </Sheet>
      <SearchDialog open={search} onOpenChange={setSearch} />
    </header>
  );
}
